import { useEffect, useRef } from 'react';
import useNodeLogo from '../hooks/use_node_logo';
import { NodeAvatar } from '../components/Node/NodeAvatar';

/** Mounted once at the router root. Names the browser tab and points the
 * favicon at this node, replacing the static "Vellby" title and favicon.ico
 * baked into public/index.html — one web image serves every node, so
 * neither can be decided before runtime.
 *
 * Identity comes from useNodeLogo, so the tab agrees with the navbar: the
 * uploaded logo when there is one and it loads, else the node's generated
 * avatar. There is no per-node table in this repo any more — a node looks
 * like itself from /info alone.
 *
 * The generated avatar is rendered into a hidden span and its <svg> read
 * back out as a data URI: boring-avatars only ships React components, and
 * pulling react-dom/server into the bundle for one icon costs ~50KB.
 */

// A <link rel="icon"> fires no usable load/error event, so an uploaded logo
// is vetted by decoding it as an image first and only swapped in once it
// loads; if it 404s (bucket moved, object deleted) the generated avatar is
// used instead, same as NodeLogo.jsx does for the navbar.
function probe(src, onLoad, onFail, isAlive) {
    const img = new Image();
    img.onload = () => { if (isAlive()) onLoad(); };
    img.onerror = () => { if (isAlive()) onFail(); };
    img.src = src;
}

function setFavicon(href) {
    // index.html ships the link; creating one is only insurance against a
    // future template that doesn't.
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    // Firefox won't render an SVG icon without the type, and an inherited type
    // on a later PNG would be worse than none — so set it or clear it, always.
    link.type = /^data:image\/svg\+xml|\.svg(\?|#|$)/i.test(href) ? 'image/svg+xml' : '';
    link.href = href;
}

function DocumentTitle() {
    const { logoAlt, logoUrl, nodeKey, avatar } = useNodeLogo();
    const hidden = useRef(null);

    useEffect(() => { document.title = logoAlt; }, [logoAlt]);

    useEffect(() => {
        // Nothing to draw until /info has said who this node is: swapping in
        // a "local" avatar first would flash a wrong icon on every load.
        if (!nodeKey) return undefined;
        let alive = true;
        const generated = () => {
            const svg = hidden.current?.querySelector('svg');
            if (svg) setFavicon(`data:image/svg+xml;utf8,${encodeURIComponent(svg.outerHTML)}`);
        };
        if (logoUrl) probe(logoUrl, () => setFavicon(logoUrl), generated, () => alive);
        else generated();
        return () => { alive = false; };
    }, [logoUrl, nodeKey, avatar]);

    // Off-screen, not display:none — either works for reading outerHTML, but
    // hidden from assistive tech and layout alike.
    return nodeKey ? (
        <span ref={hidden} style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
            <NodeAvatar serverId={nodeKey} avatar={avatar} size={64} />
        </span>
    ) : null;
}

export default DocumentTitle;
